import EmptyState from "@/components/empty-state/EmptyState.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import {
  ConfirmationModal,
  Page,
  PaginationBlock,
  useModals,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Table,
  TableCell,
} from "@skailer-com/uikit";
import { Search } from "@/components/search/Search.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { DivisionEditModal } from "@/pages/settings/divisions/widgets/DivisionEditModal.tsx";
import { useDivisionDelete, useDivisionsSearch } from "@/queries/use-divisitions.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import {
  ApiDivision,
  ApiDivisionFilter,
  ApiDivisionsFilterDefault,
} from "@/types/division.types.ts";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce, useEffectOnce } from "react-use";

export default function Divisions() {
  const { t } = useTranslation();
  useTitle(t("core:divisions"), t("common:settings"));

  const { openModal } = useModals();

  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState<ApiDivisionFilter>(ApiDivisionsFilterDefault);
  useDebounce(
    () => {
      setFilter({
        page_number: 1,
        text: searchTerm,
      });
    },
    300,
    [searchTerm]
  );
  const { data: divisions, isLoading: divisionsIsLoading, isFetching } = useDivisionsSearch(filter);
  const divisionDelete = useDivisionDelete();

  const columns = useMemo<ColumnDef<ApiDivision>[]>(
    () => [
      {
        header: t("common:title"),
        cell: (row) => <TableCell>{row.row.original.title}</TableCell>,
        accessorKey: "title",
        ...ColumnSizes.employee,
      },
      {
        header: t("core:employees"),
        cell: (row) => <div className={"text-right px-2"}>{row.row.original.employees_count}</div>,
        accessorKey: "employees_count",
        ...ColumnSizes.status,
      },
    ],
    [divisions]
  );

  const handleEditOrCreate = useCallback((division: ApiDivision | undefined = undefined) => {
    const divisionEdit = division || {
      id: 0,
      title: "",
      employees_count: 0,
    };
    openModal(DivisionEditModal, {
      division: divisionEdit,
    });
  }, []);

  const handleDeleteAction = useCallback((division: ApiDivision) => {
    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:delete"),
      onAccept: async function () {
        try {
          await divisionDelete.mutateAsync(division.id, {
            onSuccess: () => {
              analyticsService.trackEvent(analyticEvents.divisions.deleted, {
                [analyticProperties.id]: division.id,
              });
            },
          });
        } catch {
          return;
        }
      },
    });
  }, []);

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.divisions.viewed);
  });

  const renderRowAction = useCallback(
    (row: Row<ApiDivision>) => (
      <DropdownMenu>
        <DropdownMenuItem
          onClick={() => {
            handleEditOrCreate(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen"} />}
          title={t("common:edit")}
        />
        <DropdownMenuItem
          onClick={() => {
            handleDeleteAction(row.original);
          }}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
          variant={"menuDanger"}
          title={t("common:delete") + "..."}
        />
      </DropdownMenu>
    ),
    [handleDeleteAction, handleEditOrCreate, t]
  );

  return (
    <>
      <Page className="h-full">
        <Page.Header
          showBack={true}
          title={t("core:divisions")}
          navigateTo={"/settings/company-structure"}
        >
          <Stack>
            <Button
              onClick={() => {
                handleEditOrCreate();
              }}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
            >
              {t("common:create_new")}
            </Button>
          </Stack>
        </Page.Header>
        <Page.Content stretch>
          <Page.StickyLayout
            toolbar={
              <Stack direction={"horizontal"}>
                <Search
                  onInput={(e) => {
                    setSearchTerm(e.currentTarget.value);
                  }}
                  value={searchTerm}
                  type={"text"}
                  placeholder={t("common:search_by_name")}
                  className={"w-96"}
                />
              </Stack>
            }
            footer={
              <PaginationBlock
                metadata={divisions?.metadata}
                onPageChange={(page) => {
                  setFilter({ ...filter, page_number: page });
                }}
              />
            }
          >
            <Table
              stickyHeader={true}
              renderRowAction={renderRowAction}
              data={divisions?.items || []}
              columns={columns}
              isFetching={isFetching || divisionDelete.isPending}
              showSkeleton={divisionsIsLoading}
              showNotFoundError={filter.text != "" && divisions?.metadata.total_items == 0}
              notFoundComponent={<EmptyStateNothingFound />}
              emptyStateComponent={<EmptyState />}
            />
          </Page.StickyLayout>
        </Page.Content>
      </Page>
    </>
  );
}
